import React, { useState } from "react";
import { Paperclip, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-black/20 focus:border-bd-gold outline-none py-3 text-bd-black placeholder:text-black/35 transition-colors";
const inputDarkCls =
  "w-full bg-transparent border-b border-white/20 focus:border-bd-gold outline-none py-3 text-bd-ivory placeholder:text-white/35 transition-colors";

export default function EnquiryForm({
  category = "General",
  dark = false,
  showCompany = false,
  showPosition = false,
  showPhone = false,
  showPartnershipType = false,
  partnershipTypes = [],
  showVenture = false,
  ventures = [],
  showAttachment = false,
  confidential = false,
  categories = null,
  submitLabel = "Submit Enquiry",
  intro = null,
}) {
  const cls = dark ? inputDarkCls : inputCls;
  const [form, setForm] = useState({
    category, name: "", email: "", company: "", position: "", phone: "",
    partnership_type: partnershipTypes[0] || "", venture_of_interest: "", message: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setBusy(true);
    try {
      let attachment_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/enquiry", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_id = data.id;
      }
      await api.post("/enquiries", { ...form, attachment_id, confidential });
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className={`text-center py-12 ${dark ? "text-bd-ivory" : "text-bd-black"}`}>
        <CheckCircle2 className="mx-auto mb-4 text-bd-gold" size={48} strokeWidth={1} />
        <h3 className="font-serif text-3xl mb-3">Enquiry received</h3>
        <p className={dark ? "text-bd-ivory/60" : "text-black/60"}>
          Thank you. A member of the group will respond in due course.
        </p>
      </div>
    );
  }

  const textColor = dark ? "text-bd-ivory/50" : "text-black/50";

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-6">
      {intro}
      {categories && (
        <div>
          <label className={`overline block mb-1 ${textColor}`}>Enquiry Type</label>
          <select data-testid="enquiry-category" value={form.category} onChange={set("category")} className={cls}>
            {categories.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="enquiry-name" className={cls} placeholder="Full name" value={form.name} onChange={set("name")} />
        <input data-testid="enquiry-email" type="email" className={cls} placeholder="Email address" value={form.email} onChange={set("email")} />
      </div>
      {(showCompany || showPosition) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {showCompany && <input data-testid="enquiry-company" className={cls} placeholder="Company" value={form.company} onChange={set("company")} />}
          {showPosition && <input data-testid="enquiry-position" className={cls} placeholder="Position" value={form.position} onChange={set("position")} />}
        </div>
      )}
      {showPhone && <input data-testid="enquiry-phone" className={cls} placeholder="Phone" value={form.phone} onChange={set("phone")} />}
      {showPartnershipType && (
        <div>
          <label className={`overline block mb-1 ${textColor}`}>Type of Partnership</label>
          <select data-testid="enquiry-partnership-type" value={form.partnership_type} onChange={set("partnership_type")} className={cls}>
            {partnershipTypes.map((t) => <option key={t} value={t} className="text-black">{t}</option>)}
          </select>
        </div>
      )}
      {showVenture && (
        <div>
          <label className={`overline block mb-1 ${textColor}`}>Business / Venture of Interest</label>
          <select data-testid="enquiry-venture" value={form.venture_of_interest} onChange={set("venture_of_interest")} className={cls}>
            <option value="" className="text-black">— Select (optional) —</option>
            {ventures.map((v) => <option key={v.id || v.name} value={v.name} className="text-black">{v.name}</option>)}
            <option value="Group / Multiple" className="text-black">Group / Multiple</option>
          </select>
        </div>
      )}
      <textarea data-testid="enquiry-message" rows={5} className={cls} placeholder="Your message" value={form.message} onChange={set("message")} />

      {showAttachment && (
        <label data-testid="enquiry-attachment" className={`flex items-center gap-3 cursor-pointer text-sm ${dark ? "text-bd-ivory/60" : "text-black/60"} hover:text-bd-gold transition-colors`}>
          <Paperclip size={16} strokeWidth={1.5} />
          {file ? file.name : "Attach a document (optional, max 10MB)"}
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.csv,.txt" />
        </label>
      )}

      {confidential && (
        <p className={`text-xs ${dark ? "text-bd-ivory/40" : "text-black/40"}`}>
          Submissions are treated as strictly confidential and reviewed privately by the group.
        </p>
      )}

      <button
        type="submit"
        data-testid="enquiry-submit"
        disabled={busy}
        className="inline-flex items-center gap-2 bg-bd-gold text-bd-black hover:bg-bd-gold2 transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase disabled:opacity-60"
      >
        {busy && <Loader2 size={16} className="animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}
